import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// TODO: DevTools comentado temporariamente devido a erro de módulo
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// Páginas do Sistema (TSX)
import { LoginPage } from './pages/LoginPage.tsx';
import { ForcePasswordChangePage } from './pages/ForcePasswordChangePage.tsx';
import { SelectionPage } from './pages/SelectionPage.tsx';
import { SelectionClientPage } from './pages/SelectionClientPage.tsx';
import { Dashboard } from './pages/Dashboard.tsx';

// Lazy load CalendarioPage para melhor performance
const CalendarioPage = lazy(() => import('./pages/CalendarioPage.tsx').then(module => ({ default: module.CalendarioPage })));
const DownloadsPage = lazy(() => import('./pages/DownloadsPage.tsx').then(m => ({ default: m.DownloadsPage })));
import HospitaisPage from './pages/HospitaisPage.tsx';
import EstabelecimentosPage from './pages/EstabelecimentosPage.tsx';
import { ProdutosPage } from './pages/ProdutosPage.tsx';
import { ContagensPage } from './pages/ContagensPage.tsx';
import { UsuariosPage } from './pages/UsuariosPage.tsx';
import UsersPage from './pages/UsersPage.tsx';
import { RelatoriosPage } from './pages/RelatoriosPage.tsx';
import { ConfiguracoesPage } from './pages/ConfiguracoesPage.tsx';
import { MobileEmulatorPage } from './pages/MobileEmulatorPage.tsx';
import { MobileFlutterPage } from './pages/MobileFlutterPage.tsx';
// REMOVED: Mockup pages bloqueadas - apenas dados reais
// import { MobileFlutterMockupPage } from './pages/MobileFlutterMockupPage.tsx';
import { BuildApkPage } from './pages/BuildApkPage.tsx';
import { ProductBarcodePage } from './pages/ProductBarcodePage.tsx';
import { BarcodeValidationPage } from './pages/BarcodeValidationPage.tsx';
import { CNESConsultaPage } from './pages/CNESConsultaPage.tsx';
import { RepresentantesPage } from './pages/RepresentantesPage.tsx';
import { ContagemDetailsPage } from './pages/ContagemDetailsPage.tsx';
import ClientesPage from './pages/ClientesPage.tsx';
import ClienteDetailsPage from './pages/ClienteDetailsPage.tsx';
import APITestPage from './pages/APITestPage.tsx';
import { ConferenciaPage } from './pages/ConferenciaPage.tsx';
import { ConferenciaDetailPage } from './pages/ConferenciaDetailPage.tsx';
// REMOVED: Mockup pages bloqueadas - apenas dados reais
// import { ConferenciaMockupPage } from './pages/ConferenciaMockupPage.tsx';

// Páginas antigas (Job - serão substituídas futuramente)
import JobDetailsPage from './pages/JobDetailsPage.tsx';
import { JobWizardPage } from './pages/JobWizardPage.jsx';

// EPIC 19 - Sistema de Contagem Completo
import ContagemCompletaPage from './pages/ContagemCompletaPage.tsx';

// Gantt Chart Page
import { GanttChartPage } from './pages/GanttChartPage.tsx';

// Epic Escopo de Contagem
import EscopoContagemPage from './pages/EscopoContagemPage.tsx';

// Sistema de Productos Cliente (BD_produtos.xlsx)
import ProdutosClientePage from './pages/ProdutosClientePage.tsx';

// REMOVED: Mockup pages bloqueadas - apenas dados reais
// Sistema de Produtos Mockup (Dark Theme)
// import ProdutosMockupPage from './pages/ProdutosMockupPage.tsx';

// EPIC 27 - Controle de Acesso
import { ControleAcessoPage } from './pages/ControleAcessoPage.tsx';
import { PermissionRoute } from './components/PermissionRoute.tsx';

// EPIC 38 - Logs de Auditoria
import LogsPage from './pages/LogsPage.tsx';
import AccessLogsPage from './pages/AccessLogsPage.tsx';

// N129 fase 2 - Diagnosticos do app offline (MASTER only)
import DiagnosticosPage from './pages/DiagnosticosPage.tsx';
// N226 - Diagnostico Nuvem/App: tempo do servidor e das sincronizacoes (MASTER only)
import DiagnosticoNuvemPage from './pages/DiagnosticoNuvemPage.tsx';

// EPIC Bug Reports - Sistema de Registro de Ocorrencias
import BugReportsPage from './pages/BugReportsPage.tsx';

// Context Providers
import { DarkModeProvider } from './contexts/DarkModeContext.tsx';
import { SidebarProvider } from './contexts/SidebarContext.tsx';

// Sidebar Component - renderizado uma vez no App level
import { Sidebar } from './components/Sidebar.tsx';

// Story 1.9 — detecta bundle stale + forca reload
import { useVersionCheck } from './hooks/useVersionCheck';
import { VersionMismatchModal } from './components/VersionMismatchModal.tsx';

import useAuthStore from './stores/authStore';
import './App.css';
import { MantineProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { useDarkMode } from './contexts/DarkModeContext.tsx';
import 'dayjs/locale/pt-br';

/**
 * Main App Component with Conditional Sidebar Rendering
 * Sidebar is rendered once at app level, only shows on authenticated routes
 */
function AppContent() {
  const location = useLocation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Rotas públicas onde a Sidebar NÃO deve aparecer
  const publicRoutes = ['/login', '/trocar-senha', '/selection', '/selection-client', '/cnes-consulta', '/products-barcode', '/barcode-validation'];
  const isPublicRoute = publicRoutes.includes(location.pathname);

  // Mostrar Sidebar apenas se estiver autenticado E não estiver em rota pública
  const shouldShowSidebar = isAuthenticated && !isPublicRoute;

  // Story 1.9 — detecta cache stale e forca reload via modal.
  // Resolve o bug do menu fantasma da usuária.
  const versionState = useVersionCheck();

  return (
    <>
      {/* Sidebar renderizado uma vez - visível apenas em rotas autenticadas privadas */}
      {shouldShowSidebar && <Sidebar />}

      {/* Story 1.9 — Modal "Nova versao disponivel" quando bundle stale */}
      <VersionMismatchModal
        visible={versionState.isStale}
        clientVersion={versionState.clientVersion}
        serverVersion={versionState.serverVersion}
      />

      {/* Toast Container - Posicionado no canto superior direito */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            maxWidth: '400px',
          },
        }}
      />

      <Routes>
        {/* ============================================= */}
        {/* ROTAS PUBLICAS - Sem autenticacao */}
        {/* ============================================= */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/trocar-senha" element={<ForcePasswordChangePage />} />

        {/* ============================================= */}
        {/* ROTAS PROTEGIDAS - PermissionRoute verifica:  */}
        {/* 1. Autenticacao                               */}
        {/* 2. pode_visualizar por tela (Epic 27)         */}
        {/* Rotas sem mapeamento = todos autenticados     */}
        {/* MASTER role tem bypass automatico             */}
        {/* ============================================= */}
        <Route element={<PermissionRoute />}>

          {/* Rotas sem mapeamento de tela - acessiveis a todos autenticados */}
          <Route path="/selection" element={<SelectionPage />} />
          <Route path="/selection-client" element={<SelectionClientPage />} />
          <Route path="/cnes-consulta" element={<CNESConsultaPage />} />
          <Route path="/products-barcode" element={<ProductBarcodePage />} />
          <Route path="/barcode-validation" element={<BarcodeValidationPage />} />

          {/* Admin Tools - mapeados para CONFIGURACOES_VIEW */}
          <Route path="/mobile/emulator" element={<MobileEmulatorPage />} />
          <Route path="/mobile/flutter" element={<MobileFlutterPage />} />
          <Route path="/mobile/flutter-mockup" element={<Navigate to="/mobile/flutter" replace />} />
          <Route path="/build-apk" element={<BuildApkPage />} />
          <Route path="/api-test" element={<APITestPage />} />
          <Route path="/cloud/produtos_mockup" element={<Navigate to="/cloud/produtos" replace />} />

          {/* Rotas Cloud - Verificadas por pode_visualizar via ROUTE_TELA_MAP */}
          <Route path="/cloud/dashboard" element={<Dashboard />} />
          <Route path="/cloud/calendario" element={
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>}>
              <CalendarioPage />
            </Suspense>
          } />
          <Route path="/cloud/gantt-chart" element={<GanttChartPage />} />
          <Route path="/cloud/hospitais" element={<HospitaisPage />} />
          <Route path="/cloud/conferencia" element={<ConferenciaPage />} />
          <Route path="/cloud/conferencia/:id" element={<ConferenciaDetailPage />} />
          <Route path="/cloud/conferencia-mockup" element={<Navigate to="/cloud/conferencia" replace />} />
          <Route path="/cloud/produtos" element={<ProdutosPage />} />
          <Route path="/cloud/cadastroestabelecimentos" element={<EstabelecimentosPage />} />
          <Route path="/cloud/clientes" element={<ClientesPage />} />
          <Route path="/cloud/clientes/:id" element={<ClienteDetailsPage />} />
          <Route path="/cloud/contagens" element={<ContagensPage />} />
          <Route path="/cloud/contagens/:id" element={<ContagemCompletaPage />} />
          <Route path="/cloud/usuarios" element={<Navigate to="/cloud/users" replace />} />
          <Route path="/cloud/users" element={<UsersPage />} />
          <Route path="/cloud/relatorios" element={<RelatoriosPage />} />
          <Route path="/cloud/downloads" element={
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>}>
              <DownloadsPage />
            </Suspense>
          } />
          <Route path="/cloud/controle-acesso" element={<ControleAcessoPage />} />
          <Route path="/cloud/logs" element={<LogsPage />} />
          <Route path="/cloud/logs/access" element={<AccessLogsPage />} />
          <Route path="/cloud/logs/diagnostico-nuvem" element={<DiagnosticoNuvemPage />} />
          <Route path="/cloud/diagnosticos" element={<DiagnosticosPage />} />
          <Route path="/cloud/bug-reports" element={<BugReportsPage />} />
          <Route path="/cloud/configuracoes" element={<ConfiguracoesPage />} />
          <Route path="/cloud/escopo-contagem" element={<EscopoContagemPage />} />

          {/* Job Routes (Legacy) */}
          <Route path="/jobs/new" element={<JobWizardPage />} />
          <Route path="/jobs/:id" element={<JobDetailsPage />} />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/selection-client" replace />} />

          {/* 404 */}
          <Route
            path="*"
            element={
              <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                  <h1 className="text-6xl font-bold text-gray-900 dark:text-white mb-2">404</h1>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">Pagina nao encontrada</p>
                  <a href="/cloud/dashboard" className="text-blue-600 hover:text-blue-700 font-semibold">
                    Voltar ao Dashboard
                  </a>
                </div>
              </div>
            }
          />

        </Route>
      </Routes>
    </>
  );
}

/**
 * Create QueryClient with optimized defaults
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      retry: 1,
    },
  },
});

function ThemedMantineProvider({ children }) {
  const { isDarkMode } = useDarkMode();
  return (
    <MantineProvider forceColorScheme={isDarkMode ? 'dark' : 'light'}>
      <DatesProvider settings={{ locale: 'pt-br', firstDayOfWeek: 0, weekendDays: [0, 6], timezone: 'America/Sao_Paulo' }}>
        {children}
      </DatesProvider>
    </MantineProvider>
  );
}

/**
 * Main App Wrapper
 */
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <DarkModeProvider>
        <ThemedMantineProvider>
          <SidebarProvider>
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </SidebarProvider>
        </ThemedMantineProvider>
      </DarkModeProvider>
      {/* TODO: Reabilitar DevTools quando módulo estiver resolvido */}
      {/* {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />} */}
    </QueryClientProvider>
  );
}

export default App;
